import { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useLazyGetCountriesQuery } from '@src/services/country/countryApiSlice';
import { useLazyGetRegionsQuery } from '@src/services/region/regionApiSlice';
import { SearchNormal } from 'iconsax-react';
import { StyledInput } from './styles';
import { selectLayout } from '@src/store/layout/layoutSlice';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

export const CountryRegionSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [countries, setCountries] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);

  const layout = useSelector(selectLayout);

  const [triggerCountries, { data: countriesData }] =
    useLazyGetCountriesQuery();
  const [triggerRegions, { data: regionsData }] = useLazyGetRegionsQuery();

  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = debounce((query: string) => {
    if (query.trim().length === 0) {
      setCountries([]);
      setRegions([]);
      return;
    }

    triggerCountries({
      acceptLanguage: 'en',
      includeRegion: true,
    });

    triggerRegions({
      acceptLanguage: 'en',
      includeCountries: true,
    });
  }, 500);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (countriesData) {
      const filteredCountries = countriesData
        .filter((country) =>
          country.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      setCountries(filteredCountries);
    }

    if (regionsData) {
      const filteredRegions = regionsData
        .filter((region) =>
          region.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      setRegions(filteredRegions);
    }
  }, [countriesData, regionsData, searchTerm]);

  const handleSelect = (item: any, type: 'country' | 'region') => {
    const paramKey = type === 'country' ? 'countryId' : 'regionId';
    navigate(`/dashboard/packages?${paramKey}=${item.id}`);
    setSearchTerm('');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className='relative w-full max-w-md'>
      <StyledInput
        type='text'
        placeholder='Where to travel?'
        suffix={<SearchNormal variant='Bulk' className='text-wl-primary' />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        variant='filled'
        className='border-none !bg-wl-background !text-wl-on-background'
        theme={layout.theme}
      />

      {searchTerm && (
        <div
          className={clsx(
            'absolute border w-full mt-1 rounded max-h-60 overflow-auto shadow',
            'bg-wl-background',
            'border-wl-surface'
          )}
          style={{ zIndex: 100 }}
        >
          {countries.length > 0 && (
            <div>
              <div
                className={clsx(
                  'text-md font-bold px-2 py-1',
                  'text-wl-on-background'
                )}
              >
                Countries
              </div>
              <ul>
                {countries.map((country, index) => (
                  <li
                    key={`country-${index}`}
                    className={clsx(
                      'p-2 cursor-pointer text-sm font-normal',
                      'text-wl-on-background',
                      'hover:bg-wl-surface'
                    )}
                    onClick={() => handleSelect(country, 'country')}
                  >
                    {country.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {regions.length > 0 && (
            <div>
              <div
                className={clsx(
                  'text-md font-bold px-2 py-1',
                  'text-wl-on-background'
                )}
              >
                Regions
              </div>
              <ul>
                {regions.map((region, index) => (
                  <li
                    key={`region-${index}`}
                    className={clsx(
                      'p-2 cursor-pointer text-sm font-normal',
                      'text-wl-on-background',
                      'hover:bg-wl-surface'
                    )}
                    onClick={() => handleSelect(region, 'region')}
                  >
                    {region.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {countries.length === 0 && regions.length === 0 && (
            <div className='flex items-center px-4 py-4 text-M-16 text-wl-on-background'>
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
